/**
 * Stripe 内嵌支付（Embedded Checkout）通用逻辑
 *
 * 网页内（/pay、章节弹窗）与游戏内（/game iframe）复用：
 * - openStripeCheckout：调内嵌接口拿 clientSecret，打开弹窗
 * - verifyStripeReceipt：支付完成后验单（仍调 verifyReceipt 确认到账）
 */
import { ref } from 'vue'
import { TOPUP_STRIPE_EMBEDDED_ENDPOINT } from '../constants'
import { api } from '@/api'

export function useStripeCheckout() {
  const stripeClientSecret = ref('')
  const stripeVisible = ref(false)
  // 验单所需：orderNum 为后端业务订单号（createPaymentIntent 返回，前端拿不到，需保存）
  const stripeOrderNum = ref('')
  // 验单所需：extendProductId 为 Stripe 商品价格 id（price_xxx），来自商品信息
  const stripeExtendProductId = ref('')

  /**
   * 从 clientSecret 解析 Stripe Checkout Session id
   * clientSecret 形如 `cs_xxx_secret_yyy`，取 `_secret_` 之前的 `cs_xxx`
   */
  function extractSessionId(clientSecret: string) {
    return clientSecret.split('_secret_')[0] || ''
  }

  /**
   * 调内嵌接口创建支付意向，成功后打开弹窗
   * @param params 创建支付意向参数
   * @param extendProductId Stripe 商品价格 id（price_xxx），用于后续验单；不传则尝试从 params 中取
   * @returns clientSecret
   */
  async function openStripeCheckout(params: Record<string, any>, extendProductId = '') {
    const { data } = await api.post<any>(TOPUP_STRIPE_EMBEDDED_ENDPOINT, params)
    const clientSecret = data?.clientSecret
    if (!clientSecret)
      throw new Error('Missing clientSecret')

    stripeClientSecret.value = clientSecret
    // orderNum 由后端在 createPaymentIntent 返回，验单必传
    stripeOrderNum.value = data?.orderNum || ''
    stripeExtendProductId.value = extendProductId || params?.extendProductId || ''
    stripeVisible.value = true
    return clientSecret
  }

  function closeStripeCheckout() {
    stripeVisible.value = false
    stripeClientSecret.value = ''
    stripeOrderNum.value = ''
    stripeExtendProductId.value = ''
  }

  /**
   * 仅更新弹窗显隐，不清空验单上下文。
   * 用于父组件响应 StripeCheckoutPopup 的 input 事件——支付完成的延迟期内用户手动关闭时，
   * 只隐藏弹窗、保留 orderNum/sessionId/extendProductId，避免随后的 complete 验单丢失上下文。
   */
  function setStripeVisible(visible: boolean) {
    stripeVisible.value = visible
  }

  /**
   * 内嵌支付完成后验单（轮询）
   *
   * 验单参数：
   * - orderNum：后端业务订单号（createPaymentIntent 返回）
   * - receipt / transactionId：均取 Stripe Checkout Session id
   * - extendProductId：Stripe 商品价格 id（price_xxx）
   */
  async function verifyStripeReceipt(maxAttempts = 15, interval = 2000): Promise<boolean> {
    const sessionId = extractSessionId(stripeClientSecret.value)
    // 验单前置校验：orderNum / extendProductId 为本次验单必需字段，
    // 缺失说明上下文异常（如后端未返回 orderNum、商品缺 extendProductId），直接判失败便于定位
    if (!sessionId || !stripeOrderNum.value || !stripeExtendProductId.value) {
      console.error('[STRIPE] 验单参数缺失', {
        sessionId,
        orderNum: stripeOrderNum.value,
        extendProductId: stripeExtendProductId.value,
      })
      return false
    }

    const query = new URLSearchParams({
      orderNum: stripeOrderNum.value,
      receipt: sessionId,
      transactionId: sessionId,
      extendProductId: stripeExtendProductId.value,
    }).toString()

    for (let i = 0; i < maxAttempts; i++) {
      try {
        const url = `/member/topup/verifyReceipt?${query}`
        const res: any = await api.post(url)
        if (res.code === 0 && res.data === true)
          return true
      }
      catch (e: any) {
        // 2003：订单处理中被加锁，继续轮询；其余错误直接判失败
        if (e?.code !== 2003)
          return false
      }
      await new Promise(resolve => setTimeout(resolve, interval))
    }
    return false
  }

  return {
    stripeClientSecret,
    stripeVisible,
    stripeOrderNum,
    stripeExtendProductId,
    openStripeCheckout,
    closeStripeCheckout,
    setStripeVisible,
    verifyStripeReceipt,
  }
}
